import { type FC, useMemo, useRef, useState } from "react";
import {
  Row,
  Box,
  Text,
  Popover,
  PopoverTrigger,
  Column,
  Portal,
  SearchInput,
  SelectorIcon,
  Tooltip,
  useDisclosure,
  TextWithTooltip,
} from "@hightouch/htui";

import { SkeletonList } from "src/components/skeletons";
import { useScrollFade } from "src/hooks/use-scroll-fade";
import {
  NAV_POPOVER_MENU_ITEM_STYLES,
  NavPopoverContentAndBody,
} from "./nav-popovers";

const selectorBg = {
  default: "rgba(255, 255, 255, 0.60)",
  hover: "white",
  open: "white",
};

const SEARCH_THRESHOLD = 5;

export interface NavSelectorOption {
  active: boolean;
  label: string;
  onClick: () => void;
}

export interface NavSelectorProps {
  ariaLabel: string;
  displayLabel: string;
  options: NavSelectorOption[];
  isNavOpen: boolean;
  height?: number;
  topAction?: {
    label: string;
    onClick: () => void;
  };
  isLoading?: boolean;
  isDisabled?: boolean;
  showSearchbar?: boolean;
  searchPlaceholder?: string;
}

export const NavSelector: FC<NavSelectorProps> = ({
  ariaLabel,
  displayLabel,
  options,
  isNavOpen,
  height = 10,
  topAction,
  isLoading = false,
  isDisabled = false,
  showSearchbar = false,
  searchPlaceholder = "Search…",
}) => {
  const [search, setSearch] = useState("");
  const { isOpen, onClose, onToggle } = useDisclosure();
  const isLargeSize = height === 10;
  const searchInputRef = useRef<HTMLInputElement>(null);

  const shouldShowSearch = showSearchbar && options.length > SEARCH_THRESHOLD;
  const scrollFade = useScrollFade();

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(search.toLowerCase()),
      ),
    [options, search],
  );

  const handleClose = () => {
    setSearch("");
    onClose();
  };

  return (
    <Popover
      isLazy
      isOpen={isOpen}
      onClose={handleClose}
      placement="bottom-start"
      initialFocusRef={shouldShowSearch ? searchInputRef : undefined}
    >
      <PopoverTrigger>
        <Row
          aria-label={ariaLabel}
          as="button"
          type="button"
          bg={isOpen ? selectorBg.open : selectorBg.default}
          borderWidth="hairline"
          borderStyle="solid"
          borderColor="base.border"
          borderRadius="lg"
          boxShadow="buttonShadowLight"
          color={isOpen ? "text.primary" : "text.tertiary"}
          height={height}
          width="100%"
          textAlign="left"
          transitionProperty="background-color, border-color, color"
          transitionDuration="fast"
          transitionTimingFunction="easeOutQuad"
          opacity={isDisabled ? 0.6 : 1}
          cursor={isDisabled ? "default" : "pointer"}
          _focusVisible={{
            borderColor: "transparent",
            outline: "none",
            boxShadow: "outline",
          }}
          _hover={
            isDisabled
              ? undefined
              : {
                  bg: isOpen ? selectorBg.open : selectorBg.hover,
                  boxShadow: "buttonShadowLight",
                  color: "text.primary",
                }
          }
          onClick={isDisabled ? undefined : onToggle}
        >
          <Tooltip
            isDisabled={isNavOpen}
            message={displayLabel}
            placement="right"
          >
            <Row
              align="center"
              justify="center"
              px={isLargeSize ? 3 : 2}
              py={isLargeSize ? 2 : 1.5}
              boxSize="100%"
            >
              {isNavOpen && (
                <Box
                  as={Text}
                  color="inherit"
                  flex={1}
                  fontSize={isLargeSize ? "md" : "sm"}
                  fontWeight="semibold"
                  whiteSpace="nowrap"
                  textOverflow="ellipsis"
                  overflow="hidden"
                >
                  {displayLabel}
                </Box>
              )}

              {!isDisabled && (
                <Box
                  flexShrink={0}
                  as={SelectorIcon}
                  color="text.tertiary"
                  boxSize={isLargeSize ? 4 : 3.5}
                />
              )}
            </Row>
          </Tooltip>
        </Row>
      </PopoverTrigger>
      <Portal>
        <NavPopoverContentAndBody width="280px">
          {shouldShowSearch && (
            <Box
              px={3}
              pt={2}
              pb={2}
              borderBottomWidth="hairline"
              borderBottomStyle="solid"
              borderColor="base.divider"
            >
              <SearchInput
                ref={searchInputRef}
                placeholder={searchPlaceholder}
                value={search}
                width="100%"
                size="md"
                onChange={(e) => setSearch(e.target.value)}
              />
            </Box>
          )}
          {isLoading ? (
            <SkeletonList gap={1.5} p={1.5}>
              <Box borderRadius="xl" h="30px" w="90%" />
              <Box borderRadius="xl" h="30px" w="70%" />
              <Box borderRadius="xl" h="30px" w="80%" />
            </SkeletonList>
          ) : filteredOptions.length === 0 && search ? (
            <Box px={3} py={4} color="text.secondary" fontSize="sm">
              No results found
            </Box>
          ) : (
            <Column
              ref={scrollFade.ref}
              mt={2}
              overflow="auto"
              maxHeight="480px"
              sx={scrollFade.sx}
            >
              {filteredOptions.map(({ active, label, onClick }) => (
                <Box
                  key={label}
                  as="button"
                  display="flex"
                  alignItems="center"
                  height={9}
                  textAlign="left"
                  bg={active ? "primary.background" : undefined}
                  role="group"
                  pointerEvents={active ? "none" : undefined}
                  _hover={{ bg: "base.background" }}
                  onClick={() => {
                    onClick();
                    handleClose();
                  }}
                  {...NAV_POPOVER_MENU_ITEM_STYLES}
                >
                  <Box overflow="hidden" flex={1} minWidth={0}>
                    <TextWithTooltip
                      color={active ? "primary.base" : "text.secondary"}
                    >
                      {label}
                    </TextWithTooltip>
                  </Box>
                </Box>
              ))}
            </Column>
          )}
          {topAction && (
            <Column
              borderTopWidth="hairline"
              borderTopStyle="solid"
              borderColor="base.divider"
            >
              <Box
                mt={2}
                as="button"
                textAlign="left"
                fontSize="md"
                lineHeight="20px"
                height={9}
                color="text.secondary"
                _hover={{ bg: "base.background", color: "text.primary" }}
                onClick={() => {
                  topAction.onClick();
                  handleClose();
                }}
                {...NAV_POPOVER_MENU_ITEM_STYLES}
              >
                {topAction.label}
              </Box>
            </Column>
          )}
        </NavPopoverContentAndBody>
      </Portal>
    </Popover>
  );
};
